import { DISTRIBUTORS_HUB } from '@cannect/constants/products'
import { usePrepareCart } from '@cannect/hooks/usePrepareCart'
import { StyledCheckbox } from '@cannect/pages/AppointmentScheduling/FormSteps/CheckoutStep/PaymentStep/styles'
import axios from 'axios'
import { CircleAlert } from 'lucide-react'
import useMediaQuery from 'hooks/useMediaQuery'
import { noop } from 'lodash'
import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { MOBILE_WIDTH } from 'utils/constants'
import { clearMask, formatZipCode } from 'utils/formatters'
import { AddressSchema, AddressSchemaType } from '../utils/addressSchema'
import FormAddress from './FormAddress'
import * as Style from './styles'
import { orderHasPillItem, PILL_DELIVERY_CEP_TEXT } from '@cannect/utils/deliveryEstimate'

export type AddressProps = AddressSchemaType

interface AddressType {
  setIsAllAddress: Dispatch<SetStateAction<boolean>>
  handleAddress: any
  hasAssociationProducts?: boolean
}

const formatEstimatedRangeText = (start?: string | null, end?: string | null) => {
  if (!start || !end) return null

  const [startYear, startMonth, startDay] = start.split('-')
  const [endYear, endMonth, endDay] = end.split('-')

  if (!startYear || !startMonth || !startDay || !endYear || !endMonth || !endDay) return null

  return `A entrega estimada é entre ${startDay}/${startMonth}/${startYear} e ${endDay}/${endMonth}/${endYear}.`
}

export function Address({ setIsAllAddress, handleAddress, hasAssociationProducts = false }: AddressType) {
  const [activeBilling, setActiveBilling] = useState(false)
  const [billingAddress, setBillingAddress] = useState<AddressProps>({
    address_number: '',
    cep: '',
    state: '',
    city: '',
    street: '',
    district: '',
    is_discrete_shipping: false
  })
  const [shippingAddress, setShippingAddress] = useState<AddressProps>({
    address_number: '',
    cep: '',
    state: '',
    city: '',
    street: '',
    district: '',
    is_discrete_shipping: false
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const { lastOrderCreated, setDeliveryCep, validatingDeliveryCep } = usePrepareCart()
  const items = lastOrderCreated?.items || []

  const isMobile = useMediaQuery(MOBILE_WIDTH)
  const estimatedRangeText = formatEstimatedRangeText(
    lastOrderCreated?.estimated_delivery_range_start,
    lastOrderCreated?.estimated_delivery_range_end
  )
  const containsProductWithHubDistributor = items.some((item: any) =>
    DISTRIBUTORS_HUB.includes(item?.product?.distributor_id)
  )
  const containProductsMatirz = items.some((item: any) => !DISTRIBUTORS_HUB.includes(item?.product?.distributor_id))
  const hasPillItem = orderHasPillItem(lastOrderCreated)
  let deliveryWarningText = estimatedRangeText

  if (!deliveryWarningText && hasPillItem) {
    deliveryWarningText = PILL_DELIVERY_CEP_TEXT
  } else if (!deliveryWarningText && (containProductsMatirz || containsProductWithHubDistributor)) {
    deliveryWarningText = PILL_DELIVERY_CEP_TEXT
  }

  const validateAddressData = (data: AddressProps) => {
    const validationResult = AddressSchema.safeParse(data)
    if (!validationResult.success) {
      const newErrors: Record<string, string> = {}
      validationResult.error.issues.forEach((issue) => {
        newErrors[issue.path[0] as string] = issue.message
      })
      setErrors(newErrors)
      return false
    }
    setErrors({})
    return true
  }

  async function fetchZipCode(
    cep: string,
    type: 'shipping' | 'billing',
    openLoading: () => void,
    closeLoading: () => void
  ) {
    openLoading()
    try {
      const response = await axios.get(`https://viacep.com.br/ws/${cep}/json/`)
      const data = response?.data ?? {}
      if (data.erro) {
        setErrors((prev) => ({ ...prev, cep: 'CEP inválido.' }))
        return
      }

      setErrors((prev) => ({ ...prev, cep: '' }))

      const updatedAddress = {
        cep: formatZipCode(data.cep ?? ''),
        state: data.uf ?? '',
        street: data.logradouro ?? '',
        district: data.bairro ?? '',
        city: data.localidade ?? ''
      }
      if (type === 'shipping') {
        setShippingAddress((prevAddress) => {
          const next = { ...prevAddress, ...updatedAddress }
          handleAddress('shippingAddress', next)
          return next
        })
      } else {
        setBillingAddress((prevAddress) => {
          const next = { ...prevAddress, ...updatedAddress }
          handleAddress('billingAddress', next)
          return next
        })
      }
    } catch (err: any) {
      console.error(err)
      setErrors((prev) => ({ ...prev, cep: 'CEP inválido.' }))
    } finally {
      closeLoading()
    }
  }

  const changeShippingAddress = ({ fieldToChange, newValue, openLoading = noop, closeLoading = noop }: any) => {
    const newShippingAddress = { ...shippingAddress, [fieldToChange]: newValue }
    setShippingAddress(newShippingAddress)

    handleAddress('shippingAddress', newShippingAddress)
    if (fieldToChange === 'cep') {
      const cepDigits = clearMask(newValue).replace(/\D/g, '')
      if (cepDigits.length === 8) {
        setDeliveryCep(newValue)
      } else {
        setDeliveryCep('')
      }
      if (newValue.length === 9) {
        fetchZipCode(clearMask(newValue), 'shipping', openLoading, closeLoading)
      }
    }

    validateAddressData(newShippingAddress)
  }

  const changeBillingAddress = ({ fieldToChange, newValue, openLoading = noop, closeLoading = noop }: any) => {
    const newBillingAddress = { ...billingAddress, [fieldToChange]: newValue }
    setBillingAddress(newBillingAddress)

    handleAddress('billingAddress', newBillingAddress)

    if (fieldToChange === 'cep' && newValue.length === 9) {
      fetchZipCode(clearMask(newValue), 'billing', openLoading, closeLoading)
    }

    validateAddressData(newBillingAddress)
  }

  const isValidAllAddress = () => {
    const isShippingAddressValid = validateAddressData(shippingAddress)
    if (!activeBilling && isShippingAddressValid) {
      setIsAllAddress(true)
    } else if (activeBilling && isShippingAddressValid && validateAddressData(billingAddress)) {
      setIsAllAddress(true)
    } else {
      setIsAllAddress(false)
    }
  }

  useEffect(() => {
    if (!lastOrderCreated) return

    const person = lastOrderCreated?.person

    const initialShipping = {
      cep: person?.personal_cep || '',
      state: person?.personal_address_state || '',
      city: person?.personal_address_city || '',
      street: person?.personal_address || '',
      district: person?.personal_address_district || '',
      address_number: person?.personal_address_number || '',
      complement: person?.address_complement || '',
      is_discrete_shipping: lastOrderCreated?.is_discrete_shipping ?? false
    }

    if (initialShipping.cep) {
      setShippingAddress(initialShipping)
      handleAddress('shippingAddress', initialShipping)
      setDeliveryCep(initialShipping.cep)
    }
  }, [lastOrderCreated])

  useEffect(() => {
    isValidAllAddress()
  }, [shippingAddress, billingAddress, activeBilling])

  return (
    <Style.Wrapper isMobile={isMobile}>
      <Style.Container isMobile={isMobile}>
        <Style.Title>
          Endereço de entrega
          <p>*Endereço de entrega precisa ser o mesmo do comprovante de residência</p>
        </Style.Title>
        {(validatingDeliveryCep || deliveryWarningText) && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 16,
              border: '1px solid #f2dd9b',
              background: '#FFEEC299',
              borderRadius: 4,
              padding: '10px 16px',
              marginBottom: 16
            }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircleAlert size={16} color="#898483" />
            </div>
            {validatingDeliveryCep ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="delivery-estimate-spinner" />
                <p style={{ fontSize: 12, fontWeight: 400, color: '#898483', margin: 0 }}>
                  <strong>IMPORTANTE:</strong> Calculando estimativa de entrega...
                </p>
              </div>
            ) : (
              <p style={{ fontSize: 12, fontWeight: 400, color: '#898483', margin: 0 }}>
                <strong>IMPORTANTE:</strong> {deliveryWarningText}
              </p>
            )}
          </div>
        )}
        {hasAssociationProducts && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <StyledCheckbox
              checked={!!shippingAddress.is_discrete_shipping}
              onChange={(e: any) => {
                const updated = { ...shippingAddress, is_discrete_shipping: e.target.checked }
                setShippingAddress(updated)
                handleAddress('shippingAddress', updated)
              }}>
              <span>Entrega discreta (sem identificação da associação)</span>
            </StyledCheckbox>
          </div>
        )}
        <FormAddress
          isMobile={isMobile}
          address={shippingAddress}
          changeAddress={changeShippingAddress}
          errors={errors}
        />
        <>
          <Style.Title>Endereço de cobrança</Style.Title>
          <StyledCheckbox checked={activeBilling} onChange={() => setActiveBilling(!activeBilling)}>
            <span>Adicionar endereço diferente do endereço de entrega</span>
          </StyledCheckbox>
        </>
      </Style.Container>
      {activeBilling && (
        <Style.Container isMobile={isMobile}>
          <Style.Title>Endereço de cobrança</Style.Title>
          <FormAddress
            isMobile={isMobile}
            address={billingAddress}
            changeAddress={changeBillingAddress}
            errors={errors}
          />
        </Style.Container>
      )}
    </Style.Wrapper>
  )
}
